/**
 * TanStack Query keys + fetchers — Faz 8.1.
 *
 * One module owns every server endpoint binding so the query keys
 * are centralized (cache invalidation, devtools navigation). Replay
 * results carry a long stale-time because the server's LRU cache
 * already guarantees the same input → same output.
 */

import type {
  PagedEventsWire,
  ArchivedEventWire,
  LineageWire,
  PositionTimelineWire,
  ReplayResultWire,
  ReplaySnapshotWire,
} from "./types.js";
import { apiCall } from "./client.js";

/**
 * Filter inputs accepted by the timeline list endpoint.
 *
 * Mirrors `ReplayEventsQuery` from `@risk-alarm/cockpit-api-contract`
 * one-to-one (eventKind, symbol, fromTickId/toTickId, limit/offset).
 * NOTE: importing the parsed Zod type directly would couple the UI
 * bundle to Zod at runtime; the shape is duplicated as a `readonly`
 * interface for ergonomics and validated by the schema test in
 * `@risk-alarm/cockpit-api-contract/src/__tests__/replay-query.test.ts`.
 * Drift is mechanically caught by the matching server-side test
 * (`__tests__/events-query-parity.test.ts`).
 *
 * `correlationId` is intentionally NOT a filter here — operators
 * drill into a single correlation via the dedicated `/lineage/:id`
 * route. Earlier UI iterations sent a `?correlationId=...` query
 * param that the server silently ignored; that input has been
 * removed (A1 P0 fix).
 */
export interface FetchEventsArgs {
  readonly limit?: number;
  readonly offset?: number;
  readonly eventKind?: string;
  readonly symbol?: string;
  readonly fromTickId?: string;
  readonly toTickId?: string;
}

export const queryKeys = {
  events: (args: FetchEventsArgs) =>
    [
      "events",
      {
        limit: args.limit ?? 50,
        offset: args.offset ?? 0,
        eventKind: args.eventKind ?? "",
        symbol: args.symbol ?? "",
        fromTickId: args.fromTickId ?? "",
        toTickId: args.toTickId ?? "",
      },
    ] as const,
  event: (tickId: string) => ["event", tickId] as const,
  lineage: (correlationId: string) => ["lineage", correlationId] as const,
  replay: (fromTickId: string, toTickId: string) =>
    ["replay", { fromTickId, toTickId }] as const,
  state: (tickId: string) => ["state", tickId] as const,
  positionTimeline: (
    symbol: string,
    fromTickId?: string,
    toTickId?: string,
  ) =>
    [
      "positionTimeline",
      { symbol, fromTickId: fromTickId ?? "", toTickId: toTickId ?? "" },
    ] as const,
};

export const fetchEvents = async (args: FetchEventsArgs): Promise<PagedEventsWire> => {
  const limit = args.limit ?? 50;
  const offset = args.offset ?? 0;
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  params.set("offset", String(offset));
  if (args.eventKind !== undefined && args.eventKind !== "")
    params.set("eventKind", args.eventKind);
  if (args.symbol !== undefined && args.symbol !== "")
    params.set("symbol", args.symbol);
  if (args.fromTickId !== undefined && args.fromTickId !== "")
    params.set("fromTickId", args.fromTickId);
  if (args.toTickId !== undefined && args.toTickId !== "")
    params.set("toTickId", args.toTickId);
  return apiCall<PagedEventsWire>(`/api/events?${params.toString()}`);
};

export const fetchEvent = async (tickId: string): Promise<ArchivedEventWire> =>
  apiCall<ArchivedEventWire>(`/api/events/${encodeURIComponent(tickId)}`);

export const fetchLineage = async (correlationId: string): Promise<LineageWire> =>
  apiCall<LineageWire>(`/api/lineage/${encodeURIComponent(correlationId)}`);

export const fetchReplay = async (
  fromTickId: string,
  toTickId: string,
): Promise<ReplayResultWire> =>
  apiCall<ReplayResultWire>(`/api/replay`, {
    method: "POST",
    body: { fromTickId, toTickId },
  });

export const fetchState = async (tickId: string): Promise<ReplaySnapshotWire> =>
  apiCall<ReplaySnapshotWire>(`/api/state/${encodeURIComponent(tickId)}`);

export interface FetchPositionTimelineArgs {
  readonly symbol: string;
  readonly fromTickId?: string;
  readonly toTickId?: string;
  readonly limit?: number;
}

export const fetchPositionTimeline = async (
  args: FetchPositionTimelineArgs,
): Promise<PositionTimelineWire> => {
  const params = new URLSearchParams();
  if (args.fromTickId !== undefined && args.fromTickId !== "")
    params.set("fromTickId", args.fromTickId);
  if (args.toTickId !== undefined && args.toTickId !== "")
    params.set("toTickId", args.toTickId);
  if (args.limit !== undefined) params.set("limit", String(args.limit));
  const qs = params.toString();
  const suffix = qs.length > 0 ? `?${qs}` : "";
  return apiCall<PositionTimelineWire>(
    `/api/positions/${encodeURIComponent(args.symbol)}/timeline${suffix}`,
  );
};
